import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import Footer from '../../components/Footer';

const Racking = () => {
  const [showGallery, setShowGallery] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  // Gallery images - optimized WebP format
  const galleryImages = [
    '/gallery/racking/racking-blue-orange-aisle.webp',
    '/gallery/racking/racking-beams-decking-detail.webp',
    '/gallery/racking/racking-red-heavy-duty.webp',
    '/gallery/racking/racking-wooden-decking-rows.webp',
    '/gallery/racking/racking-dividers-sections.webp',
    '/gallery/racking/racking-multiple-levels.webp',
    '/gallery/racking/racking-red-installation.webp',
    '/gallery/racking/racking-red-wall-mounted.webp',
    '/gallery/racking/racking-end-protectors.webp',
    '/gallery/racking/racking-warehouse-installation.webp',
    '/images/products/racking.webp',
    '/images/products/warehouse.webp',
  ];

  const features = [
    'Load capacity up to 4,000kg per level',
    'Height adjustable in 50mm increments',
    'Powder-coated or galvanized finish',
    'SABS & ISO 9001 certified',
    'Professional installation included',
    'Compatible with all pallet types',
    'Optional column guards and safety accessories',
  ];

  const applications = [
    'Warehouses & Distribution Centers',
    'Manufacturing Facilities',
    'Cold Storage',
    'Retail Storage',
    'Archive Storage',
  ];

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="w-full bg-charcoal py-16 lg:py-24">
        <div className="px-6 lg:px-[7vw]">
          <Link to="/products" className="text-white/60 hover:text-white text-sm mb-4 inline-block">
            ← Back to Products
          </Link>
          <h1
            className="font-display font-bold text-white mb-4"
            style={{ fontSize: 'clamp(32px, 5vw, 64px)', lineHeight: 1.1 }}
          >
            RACKING THAT HOLDS UP.
          </h1>
          <p className="text-white/60 max-w-2xl text-lg">
            Pallet racking systems engineered for your load, height and throughput.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="w-full bg-offwhite py-12 lg:py-20">
        <div className="px-6 lg:px-[7vw]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left - Image */}
            <div>
              <div 
                className="aspect-video rounded-2xl overflow-hidden mb-4 cursor-pointer"
                onClick={() => galleryImages.length > 0 && setShowGallery(true)}
              >
                <img
                  src="/racking_aisle.jpg"
                  alt="Pallet Racking Systems"
                  width="800"
                  height="450"
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-cover hover:scale-105 transition-transform"
                />
              </div>
              {galleryImages.length > 0 && (
                <p className="text-center text-charcoal/50 text-sm">
                  Click image to view gallery ({galleryImages.length} photos)
                </p>
              )}
            </div>

            {/* Right - Content */}
            <div>
              <h2 className="font-display font-bold text-charcoal text-2xl lg:text-3xl mb-4">
                Pallet Racking Systems
              </h2>
              <p className="text-charcoal/70 mb-6 leading-relaxed">
                Selective, drive-in, cantilever and push-back configurations—engineered for your load, 
                height and throughput. Our racking systems maximize vertical space while ensuring safe, 
                efficient access to your inventory.
              </p>

              {/* Features */}
              <h3 className="font-display font-bold text-charcoal text-lg mb-3">Key Features</h3>
              <ul className="space-y-2 mb-6">
                {features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-charcoal/80">
                    <Check className="w-4 h-4 text-lime flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Applications */}
              <h3 className="font-display font-bold text-charcoal text-lg mb-3">Applications</h3>
              <div className="flex flex-wrap gap-2 mb-8">
                {applications.map((app, idx) => (
                  <span key={idx} className="bg-charcoal/5 text-charcoal/70 px-3 py-1 rounded-full text-sm">
                    {app}
                  </span>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex gap-4 flex-wrap">
                <Link
                  to="/contact"
                  className="bg-lime text-charcoal px-6 py-3 rounded-lg font-medium inline-flex items-center gap-2 hover:bg-lime/90 transition-colors"
                >
                  Request a Quote
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="tel:+27829713299"
                  className="bg-charcoal text-white px-6 py-3 rounded-lg font-medium inline-flex items-center gap-2 hover:bg-charcoal/80 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  Call Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="w-full bg-white py-12 lg:py-16">
        <div className="px-6 lg:px-[7vw]">
          <h3 className="font-display font-bold text-charcoal text-xl mb-6">Related Products</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/products/shelving" className="group">
              <div className="aspect-video rounded-xl overflow-hidden mb-3">
                <img src="/shelving_bay.jpg" alt="Shelving" width="400" height="225" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </div>
              <h4 className="font-display font-bold text-charcoal">Industrial Shelving</h4>
            </Link>
            <Link to="/products/mezzanine" className="group">
              <div className="aspect-video rounded-xl overflow-hidden mb-3">
                <img src="/mezzanine_install.jpg" alt="Mezzanine" width="400" height="225" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </div>
              <h4 className="font-display font-bold text-charcoal">Mezzanine Floors</h4>
            </Link>
            <Link to="/products/storage" className="group">
              <div className="aspect-video rounded-xl overflow-hidden mb-3">
                <img src="/lockers_storage.jpg" alt="Storage" width="400" height="225" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </div>
              <h4 className="font-display font-bold text-charcoal">Lockers & Storage</h4>
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery Modal */}
      {showGallery && galleryImages.length > 0 && (
        <div className="fixed inset-0 z-[200] bg-charcoal/95 flex items-center justify-center p-4">
          <button
            onClick={() => setShowGallery(false)}
            aria-label="Close gallery"
            className="absolute top-4 right-4 text-white w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
          >
            ✕
          </button>
          <div className="max-w-4xl max-h-[80vh]">
            <img
              src={galleryImages[currentImage]}
              alt={`Gallery ${currentImage + 1}`}
              className="w-full h-full object-contain max-h-[80vh]"
            />
            <div className="flex justify-center gap-4 mt-4">
              <button
                onClick={() => setCurrentImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
                aria-label="Previous image"
                className="text-white bg-white/10 p-2 rounded"
              >
                <ChevronLeft />
              </button>
              <span className="flex items-center text-white">
                {currentImage + 1} / {galleryImages.length}
              </span>
              <button
                onClick={() => setCurrentImage((prev) => (prev + 1) % galleryImages.length)}
                aria-label="Next image"
                className="text-white bg-white/10 p-2 rounded"
              >
                <ChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Racking;
